import type { Mention } from '@tunasong/models'
import { shortUuid } from '@tunasong/models'
import type { Comment, Profile } from '@tunasong/schemas'
import { displayName } from './readiness.js'
import { formatVersionDate } from './venue-sheet.js'

/**
 * Comments that ask bandmates for something on the rider. They reuse the existing mention path: a mention element in
 * a comment becomes a `mentions` edge (graph-events mention-task) and a notification to the mentioned user id
 * (edges/mention). Delivery looks the recipient up server-side, so the element's `email` is display-only; `name` is
 * always set, because the mention renders `@{name ?? email}` and bandmates' public profiles carry no email.
 */

/** Enough of a profile to mention someone; a bandmate whose profile did not load is still a user id */
export type Person = Pick<Profile, 'userId'> & Partial<Pick<Profile, 'name' | 'email' | 'picture'>>

const mention = (person: Person, authorId: string): Mention => ({
  id: shortUuid(),
  type: 'mention',
  mentionerId: authorId,
  userId: person.userId,
  name: displayName(person),
  email: person.email ?? '',
  ...(person.picture ? { picture: person.picture } : {}),
  children: [{ text: '' }],
})

const comment = (riderId: string, authorId: string, children: unknown[]): Comment => ({
  type: 'comment',
  kind: 'chat',
  parentId: riderId,
  userId: authorId,
  children: [{ type: 'line', children }],
})

/** "@Ana @Tom, could you add your stage setup? It goes straight into this rider." */
export const buildRemindComment = ({
  riderId,
  authorId,
  missing,
}: {
  riderId: string
  authorId: string
  missing: Person[]
}): Comment =>
  comment(riderId, authorId, [
    { text: '' },
    ...missing.flatMap((person, index) => [mention(person, authorId), { text: index < missing.length - 1 ? ' ' : '' }]),
    { text: ', could you add your stage setup? It goes straight into this rider.' },
  ])

/** "@Kim, I updated my stage setup on 16 Oct 2026. Blue Note rider still has the old one." */
export const buildShowLeadComment = ({
  riderId,
  authorId,
  lead,
  riderName,
  updatedAt,
  locale,
}: {
  riderId: string
  authorId: string
  lead: Person
  riderName: string
  updatedAt: string
  locale?: string
}): Comment =>
  comment(riderId, authorId, [
    { text: '' },
    mention(lead, authorId),
    {
      text: `, I updated my stage setup on ${formatVersionDate(updatedAt, locale) ?? 'a recent date'}. ${riderName} still has the old one.`,
    },
  ])
